import { observer } from 'mobx-react-lite'
import React, { useEffect, useRef, useState } from 'react'
import { FiChevronLeft } from 'react-icons/fi'
import { useNavigate, useParams } from 'react-router-dom'

import AlwayzImage from '../components/molecules/AlwayzImage'
import AltoonTagComponent from '../components/templates/AltoonTagComponent'
import THImageText from '../components/molecules/THImageText'
import UserStore from '../stores/UserStore'
import backendApis from '../utils/backendApis'
import resize from '../utils/resize'
import NovelChapterComponent from '../components/templates/NovelChapterComponent'
import LoadingIndicator from '../components/atoms/loadingIndicator'
import ToastStore from '../stores/ToastStore'

const NovelDetailScreen = observer(() => {
  const { novelId } = useParams()

  const navigate = useNavigate()
  const scrollContainerRef = useRef(null)

  const [novelData, setNovelData] = useState()
  const [chapterInfo, setChapterInfo] = useState()
  const [viewLogData, setViewLogData] = useState()
  const [openDescriptionModal, setOpenDescriptionModal] = useState(false)

  const dayNameGenerator = (date) => {
    switch (date) {
      case 'Mon':
        return '월'
      case 'Tue':
        return '화'
      case 'Wed':
        return '수'
      case 'Thu':
        return '목'
      case 'Fri':
        return '금'
      case 'Sat':
        return '토'
      case 'Sun':
        return '일'
      default:
        return '월'
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      if (!novelId || novelId === 'undefined') {
        ToastStore.toastOn({
          type: 'error',
          message: '알 수 없는 오류가 발생했어요.\n 잠시후 다시 시도해주세요',
          duration: 2000,
        })
        return
      }

      const novelData = await backendApis.loadNovelInfoById({ novelId })
      if (novelData?.status === 200) {
        setNovelData(novelData.data)
      }

      const chapterData = await backendApis.loadNovelDetail({ novelId })
      if (chapterData?.status === 200) {
        setChapterInfo(chapterData.data)
      }
    }

    fetchData()
    UserStore.setIsLoaded(true)
  }, [novelId])

  const HeaderComponent = () => {
    return (
      <section className="fixed top-0 bg-[#ffffffe0] w-full z-10 py-2 flex flex-row items-center justify-between overflow-hidden">
        <button
          type="button"
          aria-label="back"
          className="mx-2 p-2 whitespace-nowrap"
          onClick={async () => {
            navigate(-1)
          }}
        >
          <FiChevronLeft className="w-8 h-8 stroke-[0.2vw]" />
        </button>
      </section>
    )
  }

  const NovelInfoSection = () => {
    return (
      <div className="flex flex-col items-center">
        <div className="flex flex-row items-center justify-center rounded-lg">
          <AlwayzImage
            src={resize(novelData?.thumbnail, 234, 328, 'fill')}
            alt={novelData?.title}
            className="w-[40vw] h-[52vw] rounded-lg flex-shrink-0"
            lazy
          />
          <div className="w-full h-[52vw] flex flex-col pl-2">
            {novelData?.bannerImage && (
              <img
                src={novelData?.bannerImage}
                alt={novelData?.title}
                className="w-full h-[16vh] rounded-t-lg"
              />
            )}
            <div className="p-2 text-xs">
              <div className="text-lg font-semibold">{novelData?.title}</div>
              <div className="flex flex-row pb-[4vw]">
                <div>{novelData?.author}</div>
                {novelData?.weekday && (
                  <div className="text-[#999999] flex flex-row">
                    <div className="mx-2">|</div>
                    {novelData?.weekday?.map((day, index) => {
                      const dayString = dayNameGenerator(day)
                      return index === novelData.weekday.length - 1
                        ? `${dayString} 연재`
                        : `${dayString}, `
                    })}
                  </div>
                )}
              </div>

              <div className="pb-[4vw] text-[2.8vw]">
                <button
                  type="button"
                  className="flex items-start text-gray-500 text-start max-h-[20vw] overflow-clip"
                  onClick={() => {
                    setOpenDescriptionModal(true)
                  }}
                >
                  <THImageText
                    text={
                      novelData?.description?.length > 50
                        ? `${novelData?.description?.slice(0, 50)?.replace(/\\n/g, '\n')}..`
                        : novelData?.description?.replace(/\\n/g, '\n')
                    }
                  />

                  {novelData?.description?.length > 40 && (
                    <img
                      src="/images/toon/ic_detail_arrow.png"
                      alt="arrowDown"
                      className="w-[3.6vw] h-[3.6vw] ml-1"
                    />
                  )}
                </button>
              </div>
              <div className="pb-[2vw] flex flex-row text-[2.4vw] overflow-scroll flex-wrap">
                {novelData?.tagList?.hot === true && (
                  <AltoonTagComponent key={Math.random()} tag="hot" type="detailPage" />
                )}
                {novelData?.tagList?.new && (
                  <AltoonTagComponent key={Math.random()} tag="new" type="detailPage" />
                )}
                {novelData?.tagList?.mainGenre?.map((tag) => (
                  <AltoonTagComponent key={Math.random()} tag={tag} type="detailPage" />
                ))}
                {novelData?.tagList?.subGenre?.slice(0, 5).map((tag) => (
                  <AltoonTagComponent key={Math.random()} tag={tag} type="detailPage" />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const ChapterList = () => {
    return (
      <div className="pt-[4vw] pb-[10vw]">
        {chapterInfo?.map((chapter) => {
          return (
            <NovelChapterComponent
              key={chapter._id}
              type="main"
              novelData={novelData}
              viewLogData={viewLogData}
              chapterInfo={chapter}
              chapterCount={chapterInfo.length}
            />
          )
        })}
      </div>
    )
  }

  const ScrollButton = () => {
    return (
      <div type="button" className="w-[8vw] fixed bottom-[2vh] right-4">
        <button
          type="button"
          className="rounded-full bg-white shadow-md"
          onClick={() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTop = 0
            }
          }}
        >
          <img src="/images/toon/toTopIcon.png" alt="up" className="w-full" />
        </button>
        <button
          type="button"
          className="rounded-full bg-white shadow-md"
          onClick={() => {
            if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
            }
          }}
        >
          <img src="/images/toon/toBottomIcon.png" alt="up" className="w-full" />
        </button>
      </div>
    )
  }

  const ToonDescriptionModal = () => {
    return (
      <>
        {openDescriptionModal && (
          <div className="fixed inset-0 z-30 flex items-center justify-center">
            <button
              type="button"
              className="fixed top-0 w-full h-full bg-black bg-opacity-50"
              onClick={() => setOpenDescriptionModal(false)}
            />
            <div className="bg-white w-[90vw] rounded-lg p-4 max-h-[80vh] min-h-[20vh] overflow-y-auto z-40">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-bold">{novelData?.title}</h2>
                <button type="button" onClick={() => setOpenDescriptionModal(false)} className="">
                  <img
                    src="/image/general/basicIcon/close/gray.png"
                    alt="close"
                    className="w-[10vw] h-[10vw]"
                  />
                </button>
              </div>
              <div className="text-[3.6vw] whitespace-pre-wrap">
                {novelData?.description?.replace(/\\n/g, '\n')}
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  if (!UserStore.isLoaded) {
    return <LoadingIndicator />
  }

  return (
    <div
      className="w-full overflow-y-scroll pt-[8vh]"
      style={{ height: '100vh' }}
      ref={scrollContainerRef}
    >
      <HeaderComponent />
      {chapterInfo?.length > 0 && (
        <div className="px-4">
          <NovelInfoSection />
          <ChapterList />
        </div>
      )}
      <ScrollButton />
      <ToonDescriptionModal />
    </div>
  )
})

export default NovelDetailScreen
